import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Link from "../audience/ScientificLink";
import { ArrowDown, ArrowUpRight, Plus, Minus } from "lucide-react";
import SolutionDirectory from "./SolutionDirectory";
import AssayPrograms from "./AssayPrograms";
import AssayLanguageDemo from "./AssayLanguageDemo";
import AgentInfrastructureSection from "./AgentInfrastructureSection";
import ScientificDataVisual from "./ScientificDataVisual";
import { categories, solutions } from "./solutionCatalog";
import { SCIENCE_LINKS, SOLUTION_AREAS } from "../../content/siteContent";
import "./Solutions.css";

const pillars = [
  {
    id: "lab-automation",
    title: SOLUTION_AREAS.find(area => area.id === "lab-automation").title,
    description: "We develop methods and connect instruments, from plate setup through readout.",
    links: [
      ["Assays & method development", "assay-automation"],
      ["Workcell design & integration", "workcell-design-install"],
      ["Lab software & method recovery", "workflow-code-recovery"],
    ],
    aliases: ["workcell-design", "vibe-code"],
  },
  {
    id: "scientific-data",
    title: SOLUTION_AREAS.find(area => area.id === "scientific-data").title,
    description: "We reconcile CRO deliveries, instrument files and experiment records into datasets with traceable sources.",
    links: [
      ["Scientific datasets & pipelines", "scientific-dataset-engineering"],
      ["CRO data & reporting", "cro-data-harmonization"],
      ["Data for model training", "model-training-datasets"],
    ],
    aliases: [],
  },
  {
    id: "specialized-ai",
    title: SOLUTION_AREAS.find(area => area.id === "specialized-ai").title,
    description: "We evaluate agents on specific scientific tasks and connect them to your data and instruments under scientist review.",
    links: [
      ["Scientific evaluations", "agent-evaluation-harness"],
      ["Agents connected to lab instruments", "agent-runtime-infrastructure"],
      ["Scientist-supervised workflows", "supervised-agent-orchestration"],
    ],
    aliases: [],
  },
];

const directoryAnchors = new Set([
  "solution-outcome-workflow", "solution-outcome-operations",
  "scientific-software", "data-ai-solutions", "solutions-directory-list",
  ...solutions.map(({ id }) => id),
]);

const Solutions = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [directoryOpen, setDirectoryOpen] = useState(false);
  const hasDirectoryAnchor = directoryAnchors.has(location.hash.slice(1));
  const directoryExpanded = directoryOpen || hasDirectoryAnchor;

  useEffect(() => {
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute("content");
    description?.setAttribute(
      "content",
      "Lab automation, scientific data, and an agentic harness. Methods, datasets, tools and evaluations for your science."
    );
    return () => {
      if (previousDescription !== null && previousDescription !== undefined) description?.setAttribute("content", previousDescription);
    };
  }, []);

  const toggleDirectory = () => {
    if (directoryExpanded && hasDirectoryAnchor) {
      navigate({ pathname: location.pathname, search: location.search, hash: "#solutions-explorer" },
        { replace: true, state: { preserveScroll: true } });
    }
    setDirectoryOpen(!directoryExpanded);
  };

  const openService = (id) => ({
    pathname: "/solutions",
    search: `?service=${id}`,
    hash: location.hash,
  });

  return (
    <main id="main-content" className="solutions-editorial">
      <section id="solutions-hero" data-primary-nav="solutions" className="solutions-intro solutions-shell">
        <div className="solutions-hero-layout">
        <div className="solutions-hero-copy">
        <h1>Built around<br /><em>your science.</em></h1>
        <div className="solutions-intro-bottom">
          <Link to="/contact?source=solutions-hero" data-cta="discuss-project" data-solution-id="workflow-review" className="solutions-action">
            Discuss a project <ArrowUpRight aria-hidden="true" />
          </Link>
        </div>
        </div>
        <AssayPrograms compact />
        </div>
        <nav className="solutions-contents" aria-label="Solution areas">
          {pillars.map((pillar, index) => (
            <a key={pillar.id} href={`#${pillar.id}`}>
              <span className="solutions-number" aria-hidden="true">0{index + 1}</span>
              <span>{pillar.title}</span>
              <ArrowDown aria-hidden="true" />
            </a>
          ))}
        </nav>
      </section>

      <div className="solutions-pillars solutions-shell">
        {pillars.map((pillar, index) => (
          <section key={pillar.id} id={pillar.id} data-primary-nav="solutions" className="solutions-pillar" aria-labelledby={`${pillar.id}-heading`}>
            {pillar.aliases.map((alias) => <span key={alias} id={alias} className="solutions-anchor-alias" aria-hidden="true" />)}
            <div className="solutions-pillar-name">
              <span className="solutions-number" aria-hidden="true">0{index + 1}</span>
              <h2 id={`${pillar.id}-heading`}>{pillar.title}<span>.</span></h2>
            </div>
            <div className="solutions-pillar-body">
              <p>{pillar.description}</p>
              <ul className="solutions-service-links">
                {pillar.links.map(([label, id]) => (
                  <li key={id}>
                    <Link to={openService(id)} state={{ preserveScroll: true, solutionsDetailEntry: true }} data-cta="view-solution" data-solution-id={id}>
                      {label}<ArrowUpRight aria-hidden="true" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="solutions-work-visual">
              {pillar.id === "lab-automation" && <AssayLanguageDemo compact />}
              {pillar.id === "scientific-data" && <ScientificDataVisual />}
              {pillar.id === "specialized-ai" && <AgentInfrastructureSection compact />}
            </div>
          </section>
        ))}
      </div>

      <section id="solutions-explorer" data-primary-nav="solutions" className="solutions-catalog solutions-shell" aria-labelledby="catalog-heading">
        <div className="solutions-catalog-heading">
          <div>
            <h2 id="catalog-heading">Have something specific in mind?</h2>
          </div>
          <button id="solution-directory-toggle" type="button" onClick={toggleDirectory} aria-expanded={directoryExpanded} aria-controls="solution-directory-content" className="solutions-text-action">
            {directoryExpanded ? "Close service directory" : "Explore all services"}
            {directoryExpanded ? <Minus aria-hidden="true" /> : <Plus aria-hidden="true" />}
          </button>
        </div>
        <SolutionDirectory categories={categories} solutions={solutions} expanded={directoryExpanded} />
      </section>

      <section className="solutions-start" data-primary-nav="solutions" aria-labelledby="solutions-start-heading">
        <div className="solutions-shell solutions-start-layout">
          <h2 id="solutions-start-heading">Start with<br /><em>one problem.</em></h2>
          <div>
            <Link to="/contact?source=solutions-footer" data-cta="discuss-project" data-solution-id="workflow-review" className="solutions-action">
              Discuss a project <ArrowUpRight aria-hidden="true" />
            </Link>
            <Link to={SCIENCE_LINKS.workingTogether.to} className="solutions-process-link">{SCIENCE_LINKS.workingTogether.label} <ArrowUpRight aria-hidden="true" /></Link>
          </div>
        </div>
      </section>

      <aside className="solutions-scope solutions-shell" aria-labelledby="solutions-scope-heading">
        <h2 id="solutions-scope-heading">Research and development workflows</h2>
        <p>Cornucopia's current services and co-design work support research, development, and laboratory automation. Standard engagements are intended for research use. Clinical diagnosis, patient-care decisions, and regulated use require a separate written scope and customer-led qualification, validation, Quality Unit approval, and release.</p>
      </aside>
    </main>
  );
};

export default Solutions;
